import { unwrap, type Schemas } from '@swarm/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { Permission } from '../identity/api';

export type Dashboard = Schemas['DashboardDto'];
export type YieldReport = Schemas['YieldReportDto'];
export type SalesReport = Schemas['SalesReportDto'];
export type FullExport = Schemas['ExportDto'];
export type ExportList =
  | 'colonies'
  | 'locations'
  | 'treatments'
  | 'mite-counts'
  | 'feedings'
  | 'batches'
  | 'stock'
  | 'customers'
  | 'sales'
  | 'invoices'
  | 'finance'
  | 'tasks';

/** The read permission of each list (as the backend checks it). */
export const listPermission: Record<ExportList, Permission> = {
  colonies: 'colonies.read',
  locations: 'locations.read',
  treatments: 'treatments.read',
  'mite-counts': 'treatments.read',
  feedings: 'feedings.read',
  batches: 'harvest.read',
  stock: 'stock.read',
  customers: 'customers.read',
  sales: 'sales.read',
  invoices: 'invoices.read',
  finance: 'finance.read',
  tasks: 'tasks.read',
};

export const exportLists = Object.keys(listPermission) as ExportList[];

export function csvUrl(list: ExportList): string {
  return `/api/v1/exports/lists/${list}`;
}

export function annualReportUrl(year: number): string {
  return `/api/v1/reports/annual?year=${year}`;
}

export function treatmentReportUrl(from: string, to: string): string {
  return `/api/v1/reports/treatments?from=${from}&to=${to}`;
}

export function colonyProfileUrl(colony: string): string {
  return `/api/v1/colonies/${colony}/profile`;
}

export function exportDownloadUrl(id: string): string {
  return `/api/v1/exports/full/${id}/download`;
}

export function useDashboard() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => unwrap(await api.GET('/dashboard')),
  });
}

export function useYieldReport(year: number) {
  return useQuery({
    queryKey: ['reports', 'yield', year],
    queryFn: async () => unwrap(await api.GET('/reports/yield', { params: { query: { year } } })),
    placeholderData: (previous) => previous,
  });
}

export function useSalesReport(year: number) {
  return useQuery({
    queryKey: ['reports', 'sales', year],
    queryFn: async () => unwrap(await api.GET('/reports/sales', { params: { query: { year } } })),
    placeholderData: (previous) => previous,
  });
}

/** Full exports; polls while one is being built. */
export function useExports(enabled = true) {
  return useQuery({
    queryKey: ['exports'],
    queryFn: async () => unwrap(await api.GET('/exports/full')),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.some((e) => e.status === 'pending') ? 3000 : false,
  });
}

export function useRequestExport() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.POST('/exports/full')),
    onSuccess: () => client.invalidateQueries({ queryKey: ['exports'] }),
  });
}
